import { useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { sepsisEarlyRecognitionCard } from "../../../content/clinical-cards/sepsis-early-recognition";
import { ActionBar } from "../../../design-system/patterns/ActionBar";
import { WorkspaceFrame, type WorkspaceStep } from "../../../design-system/patterns/WorkspaceFrame";
import type { ContentBlock } from "../../../domain/content-schema";
import { ClinicalBlockRenderer } from "../components/ClinicalBlockRenderer";
import styles from "./SepsisEarlyRecognitionWorkspace.module.css";

type Section = Readonly<{ id: string; label: string; blocks: readonly ContentBlock[] }>;

function sectionsFrom(blocks: readonly ContentBlock[]): readonly Section[] {
  const sections: { id: string; label: string; blocks: ContentBlock[] }[] = [];
  blocks.forEach((block) => {
    if (block.type === "heading" && block.level === 2) sections.push({ id: block.anchor, label: block.text, blocks: [] });
    else sections.at(-1)?.blocks.push(block);
  });
  return sections;
}

const sections = sectionsFrom(sepsisEarlyRecognitionCard.blocks);
const steps: readonly WorkspaceStep[] = sections.map((section) => ({ id: section.id, label: section.label }));

export function SepsisEarlyRecognitionWorkspace() {
  const [params, setParams] = useSearchParams();
  const requested = params.get("bolum");
  const complete = requested === "tumu";
  const fallback = sections[0]?.id ?? "";
  const activeId = !complete && sections.some((section) => section.id === requested) ? requested! : fallback;
  const activeIndex = Math.max(0, sections.findIndex((section) => section.id === activeId));
  const lastPanel = useRef(fallback);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const rendered = useMemo<readonly Section[]>(() => {
    if (complete) return sections;
    const active = sections[activeIndex];
    return active ? [active] : [];
  }, [activeIndex, complete]);

  useEffect(() => { document.title = "Çocukta Sepsisin Erken Tanınması | PedEmUP"; }, []);
  useEffect(() => { if (!complete) lastPanel.current = activeId; headingRef.current?.focus({ preventScroll: true }); }, [activeId, complete]);

  const select = (id: string) => setParams({ bolum: id });
  return <WorkspaceFrame
    eyebrow="Klinik Kart · yapılandırılmış sepsis pilotu"
    title={sepsisEarlyRecognitionCard.identity.title}
    summary={sepsisEarlyRecognitionCard.identity.summary}
    steps={steps}
    activeId={complete ? "" : activeId}
    onSelect={select}
    viewMode={complete ? "complete" : "step"}
    actionBar={<ActionBar
      canBack={!complete && activeIndex > 0}
      canNext={!complete && activeIndex < sections.length - 1}
      complete={complete}
      onBack={() => select(sections[activeIndex - 1]!.id)}
      onNext={() => select(sections[activeIndex + 1]!.id)}
      onReadAll={() => select(complete ? lastPanel.current : "tumu")}
    />}
  >
    <div className={styles.content}>
      {complete && <header className={styles.readAll}><p>Tam okuma görünümü</p><h2 ref={headingRef} tabIndex={-1}>Sepsis klinik kartının tümü</h2></header>}
      {rendered.map((section, sectionIndex) => <section key={section.id} aria-labelledby={`sepsis-${section.id}`}>
        <h2 id={`sepsis-${section.id}`} ref={!complete && sectionIndex === 0 ? headingRef : undefined} tabIndex={-1}>{section.label}</h2>
        {section.blocks.map((block, index) => <ClinicalBlockRenderer key={`${section.id}-${index}`} block={block} references={sepsisEarlyRecognitionCard.references} />)}
      </section>)}
      <footer className={styles.version}>
        <strong>Editoryal sürüm {sepsisEarlyRecognitionCard.review.contentVersion}</strong>
        <span>Klinik eşleme incelemesi bekliyor · Sonraki gözden geçirme: {sepsisEarlyRecognitionCard.review.nextReviewAt}</span>
      </footer>
    </div>
  </WorkspaceFrame>;
}
